import AddCardIcon from "@mui/icons-material/AddCard";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import NumbersIcon from "@mui/icons-material/Numbers";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";

import { Alert, Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import { EPaymentMethod, IPaymentBankTransfer, IPaymentOnline, TPaymentMethod } from "../../../../create/interfaces/invoice.interfaces";
import { colors, alphaVar } from "../../../../../ui/colors";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { useModals } from "../../../../../../context/ModalContext";
import { PaymentMethodSelector, usePaymentMethodState, useSetPaymentMethod } from "../../../../create/PaymentMethods";
import { SelectedPayment } from "../../../../create/PaymentMethods/types";

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface Props {
  paymentMethods: TPaymentMethod[];
  isEditable: boolean;
  isSaving: boolean;
  onSave: (nextMethods: TPaymentMethod[]) => void;
  getPaymentMethodLabel: (method: EPaymentMethod | null | undefined) => string;
  isOnlinePayment: (pm: any) => pm is IPaymentOnline;
  isBankTransfer: (pm: any) => pm is IPaymentBankTransfer;
  isBalanceFullyCoveredByDeposit: boolean;
  cardStyles: any;
  availablePayments: any;
  paypalMerchantId?: string | null;
  stripeMerchantId?: string | null;
}

const MODAL_KEY = "edit-invoice-payment-methods";

export const PaymentMethodsEditor = ({
  paymentMethods,
  isEditable,
  isSaving,
  onSave,
  getPaymentMethodLabel,
  isOnlinePayment,
  isBankTransfer,
  isBalanceFullyCoveredByDeposit,
  cardStyles,
  availablePayments,
  paypalMerchantId,
  stripeMerchantId,
}: Props) => {
  const { closeByKey } = useModals();
  const { setField, reset: resetPaymentMethod } = useSetPaymentMethod();
  const paymentState = usePaymentMethodState();

  const initForm = () => {
    resetPaymentMethod();

    const hasPaypal = paymentMethods.some((pm) => pm?.provider === EPaymentMethod.PAYPAL);
    const hasStripe = paymentMethods.some((pm) => pm?.provider === EPaymentMethod.STRIPE);
    const btMethod = paymentMethods.find((pm) => pm?.provider === EPaymentMethod.BANK_TRANSFER) as IPaymentBankTransfer | undefined;

    const selectedIds: SelectedPayment[] = [];
    if (hasPaypal) selectedIds.push("paypal");
    if (hasStripe) selectedIds.push("stripe");
    if (btMethod) selectedIds.push("bank_transfer");

    setField("selectedPayments", selectedIds);

    if (btMethod?.bankTransferInfo) {
      const btInfo = {
        accountHolderName: btMethod.bankTransferInfo.accountHolderName || "",
        accountNumber: btMethod.bankTransferInfo.accountNumber || "",
        routingNumber: btMethod.bankTransferInfo.routingNumber || "",
        address: btMethod.bankTransferInfo.address || "",
      };
      localStorage.setItem("bankTransfer.details.default", JSON.stringify(btInfo));
    } else {
      localStorage.removeItem("bankTransfer.details.default");
    }
  };

  const handleCloseModal = () => {
    closeByKey(MODAL_KEY);
  };

  const handleSave = () => {
    const selectedIds = paymentState.selectedPayments || [];
    const nextMethods: TPaymentMethod[] = [];

    if (selectedIds.includes("paypal")) {
      nextMethods.push({ provider: EPaymentMethod.PAYPAL } as IPaymentOnline);
    }
    if (selectedIds.includes("stripe")) {
      nextMethods.push({ provider: EPaymentMethod.STRIPE } as IPaymentOnline);
    }
    if (selectedIds.includes("bank_transfer")) {
      let bankInfo = {
        accountHolderName: "Bank Transfer",
        accountNumber: "",
        routingNumber: "",
        address: "",
      };
      try {
        const raw = localStorage.getItem("bankTransfer.details.default");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            bankInfo = {
              accountHolderName: parsed.accountHolderName || "Bank Transfer",
              accountNumber: parsed.accountNumber || "",
              routingNumber: parsed.routingNumber || "",
              address: parsed.address || "",
            };
          }
        }
      } catch (e) {
        console.error("Failed to read bank transfer details from localStorage:", e);
      }

      nextMethods.push({
        provider: EPaymentMethod.BANK_TRANSFER,
        bankTransferInfo: {
          accountHolderName: bankInfo.accountHolderName.trim(),
          accountNumber: bankInfo.accountNumber.trim(),
          routingNumber: bankInfo.routingNumber.trim(),
          address: bankInfo.address.trim() || undefined,
        },
      } as IPaymentBankTransfer);
    }

    onSave(nextMethods);
    handleCloseModal();
  };

  const isSaveDisabled = !paymentState.selectedPayments || isSaving;

  return (
    <Card sx={cardStyles}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700] }}>
            Payment Methods
          </Typography>
          {isEditable && (
            <BaseModalComponent
              modalKey={MODAL_KEY}
              modalLabel="Edit Payment Methods"
              defaultSizes={{ width: "500px", height: "auto" }}
              allowInsidePreventModal
              elementTrigger={
                <Button size="small" startIcon={<EditIcon />} onClick={initForm}>
                  Edit
                </Button>
              }
            >
              <Box sx={{ p: 3 }}>
                <PaymentMethodSelector availablePayments={availablePayments} canMarkAsPaid={false} />
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button onClick={handleCloseModal} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSave} disabled={isSaveDisabled}>
                    Save
                  </Button>
                </Stack>
              </Box>
            </BaseModalComponent>
          )}
        </Stack>

        {isBalanceFullyCoveredByDeposit && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Net balance after deposit credit was <strong>$0.00</strong>, so no PayPal or Stripe order was created. The invoice is closed; the PayPal
            line is the offered method only (no capture).
          </Alert>
        )}

        {(paymentMethods || []).length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.gray[500], fontStyle: "italic" }}>
            No payment methods configured.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {paymentMethods.map((pm, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alphaVar(colors.gray[200], 0.5)}`,
                bgcolor: alphaVar(colors.gray[50], 0.3),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AddCardIcon sx={{ fontSize: 20, color: colors.orange.base }} />
                  <Typography variant="body1" fontWeight={700} sx={{ color: colors.gray[700] }}>
                    {getPaymentMethodLabel(pm?.provider)}
                  </Typography>
                </Box>
                {isOnlinePayment(pm) && pm?.paidAt && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Paid"
                    size="small"
                    sx={{
                      bgcolor: colors.success[100],
                      color: colors.success[700],
                      fontWeight: 600,
                    }}
                  />
                )}
                {isBankTransfer(pm) && pm.paidAt && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Paid"
                    size="small"
                    sx={{
                      bgcolor: colors.success[100],
                      color: colors.success[700],
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              {isOnlinePayment(pm) && (
                <Stack spacing={1}>
                  {pm.paidAt && !pm.externalOrderUid && isBalanceFullyCoveredByDeposit && (
                    <Typography variant="body2" sx={{ color: colors.gray[600], fontStyle: "italic" }}>
                      Not charged online — balance covered by deposit credit.
                    </Typography>
                  )}
                  {pm.externalOrderUid && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Order ID:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700], fontFamily: "monospace" }}>
                        {pm.externalOrderUid}
                      </Typography>
                    </Box>
                  )}
                  {(pm?.externalMerchantId || (pm?.provider === EPaymentMethod.PAYPAL ? paypalMerchantId : stripeMerchantId)) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Merchant ID:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700], fontFamily: "monospace" }}>
                        {pm?.externalMerchantId || (pm?.provider === EPaymentMethod.PAYPAL ? paypalMerchantId : stripeMerchantId)}
                      </Typography>
                    </Box>
                  )}
                  {pm?.paymentUrl && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinkIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                      <Typography
                        variant="body2"
                        component="a"
                        href={pm.paymentUrl || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: colors.blue.base,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Payment URL
                      </Typography>
                    </Box>
                  )}
                  {pm.paidAt && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Paid At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.success[700] }}>
                        {formatDateTime(
                          (() => {
                            const paidAt = pm.paidAt;
                            if (!paidAt) return null;
                            if (typeof paidAt === "string") return paidAt;
                            if (paidAt instanceof Date) return paidAt.toISOString();
                            return String(paidAt);
                          })(),
                        )}
                      </Typography>
                    </Box>
                  )}
                  {pm.transactionId && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Transaction ID:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700], fontFamily: "monospace" }}>
                        {pm.transactionId}
                      </Typography>
                    </Box>
                  )}
                  {pm.fee !== null && pm.fee !== undefined && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Fee:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                        ${pm.fee?.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {pm.netAmount !== null && pm.netAmount !== undefined && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Net Amount:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                        ${pm.netAmount?.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {pm.webhookId && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Webhook ID:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[600], fontFamily: "monospace", fontSize: "0.75rem" }}>
                        {pm.webhookId}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}

              {isBankTransfer(pm) && (
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                    <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                      <strong>Account Holder:</strong> {pm.bankTransferInfo.accountHolderName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalanceIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                    <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                      <strong>Account Number:</strong> {pm.bankTransferInfo.accountNumber}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <NumbersIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                    <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                      <strong>Routing Number:</strong> {pm.bankTransferInfo.routingNumber}
                    </Typography>
                  </Box>
                  {pm.bankTransferInfo.address && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <HomeIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                      <Typography variant="body2" sx={{ color: colors.gray[700] }}>
                        <strong>Address:</strong> {pm.bankTransferInfo.address}
                      </Typography>
                    </Box>
                  )}
                  {pm.paidAt && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Paid At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.success[700] }}>
                        {formatDateTime(
                          (() => {
                            const paidAt = pm.paidAt;
                            if (!paidAt) return null;
                            if (typeof paidAt === "string") return paidAt;
                            if (paidAt instanceof Date) return paidAt.toISOString();
                            return String(paidAt);
                          })(),
                        )}
                      </Typography>
                    </Box>
                  )}
                  {pm.transactionId && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: colors.gray[500], minWidth: 120 }}>
                        Transaction Reference:
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray[700], fontFamily: "monospace" }}>
                        {pm.transactionId}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
        )}
      </Stack>
    </Card>
  );
};
